import { makePropertyMetadataClient } from './frameworkBuilder/makePropertyMetadataClient';
import { makeTypeMetadataClient } from './frameworkBuilder/makeTypeMetadataClient';
import { makeFrameworkTypesClient } from './frameworkTypes/makeFrameworkTypesClient';
import { makeGraphClient } from './graph/makeGraphClient';
import { makePropertiesClient } from './properties/makePropertiesClient';
import { makePipelinesClient } from './pipelines/makePipelinesClient';
import { makeMultiCurrencyClient } from './multiCurrency/makeMultiCurrencyClient';
import { Metrics } from './metrics';
import { createRpcClientV2 } from 'rpc-client-utils';
import { makeAssociationDefinitionsClient } from './associationDefinitions/makeAssociationDefinitionsClient';
import { makeConditionalPropertyRulesClient } from './conditionalPropertyRules/makeConditionalPropertyRulesClient';
export const makeFrameworkDataSchemaResolversClient = async ({
  httpClient
}) => {
  const rpcClient = createRpcClientV2({
    hubHttpClient: httpClient
  });
  const propertiesClientPromise = makePropertiesClient({
    httpClient
  });
  const frameworkTypesClientPromise = makeFrameworkTypesClient({
    httpClient
  });
  const typeMetadataClientPromise = makeTypeMetadataClient({
    httpClient
  });
  const propertyMetadataClientPromise = makePropertyMetadataClient({
    httpClient
  });
  const pipelinesClientPromise = makePipelinesClient({
    httpClient
  });
  const associationDefinitionsClientPromise = makeAssociationDefinitionsClient({
    rpcClient
  });
  const conditionalPropertyRulesClientPromise = makeConditionalPropertyRulesClient({
    httpClient,
    rpcClient
  });
  const multiCurrencyClientPromise = makeMultiCurrencyClient({
    httpClient
  });
  const graphClientPromise = makeGraphClient({
    propertiesClientPromise,
    frameworkTypesClientPromise,
    typeMetadataClientPromise,
    propertyMetadataClientPromise
  });
  const [properties, pipelines, frameworkTypes, typeMetadata, propertyMetadata, associations, conditionalPropertyRules, multiCurrency, graph] = await Promise.all([propertiesClientPromise, pipelinesClientPromise, frameworkTypesClientPromise, typeMetadataClientPromise, propertyMetadataClientPromise, associationDefinitionsClientPromise, conditionalPropertyRulesClientPromise, multiCurrencyClientPromise, graphClientPromise]);
  return {
    /**
     * Methods for fetching properties and property groups.
     */
    properties,
    /**
     * Methods for fetching pipelines and their stages
     */
    pipelines,
    /**
     * Methods for fetching framework type (object and event) definitions.
     */
    frameworkTypes,
    /**
     * Methods for fetching framework builder type metadata entries.
     */
    typeMetadata,
    /**
     * Methods for fetching framework builder property metadata entries.
     */
    propertyMetadata,
    /**
     * Methods for fetching association definitions.
     */
    associations,
    /**
     * Methods for fetching conditional property rules.
     */
    conditionalPropertyRules,
    /**
     * Methods for fetching multi-currency information for the portal.
     */
    multiCurrency,
    /**
     * Methods for fetching and automatically joining schemas data together.
     */
    graph,
    /**
     * Methods for operating on the client's internal state.
     */
    meta: {
      /**
       * Prints the client's internal state out to console.
       */
      debug: () => {
        properties.debug();
        frameworkTypes.debug();
        propertyMetadata.debug();
        typeMetadata.debug();
        pipelines.debug();
        associations.debug();
        conditionalPropertyRules.debug();
        multiCurrency.debug();
        graph.debug();
      },
      /**
       * Clears the client's internal caches, including any persistent caching.
       * Please be careful to only use this in tests.
       * @returns A promise which resolves when the internal state is reset.
       */
      clearCaches: async () => {
        Metrics.counter('clearCaches').increment();
        await Promise.all([properties.clearCache(), pipelines.clearCache(), frameworkTypes.clearCache(), propertyMetadata.clearCache(), typeMetadata.clearCache(), associations.clearCache(), conditionalPropertyRules.clearCache(), multiCurrency.clearCache(), graph.clearCache()]);
      }
    }
  };
};